import { onBeforeUnmount, reactive, ref } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useUserStore } from '@/stores/userStore';
import type { ApiV1 } from '@teamworksdev/influencer/core';
import { useRouter } from 'vue-router';
import { resetGainsight } from '@/plugins/gainsight';
import { resetCustomerIo } from '@/plugins/customerio';
import { usePusher } from '@/plugins/pusher';
import { $ResetPinia } from '@/plugins/pinia';
import { useInfluencer } from './api/useInfluencer';
import { handleError } from '@/utils/errorHandling';

interface UseBasicAuthOptions {
	onLogInSuccess?: () => void;
	onLogInError?: () => void;
}

export function useBasicAuth(options?: UseBasicAuthOptions) {
	const authStore = useAuthStore();
	const { unsetUser } = useUserStore();
	const router = useRouter();
	const pusher = usePusher();
	const inflcr = useInfluencer();

	const isInitLoginBasic = ref(false);

	const defaultAuthTokenRequest: ApiV1.BasicAuthRequest = {
		email: '',
		password: '',
		remember_me: false,
	};
	const authTokenRequest: ApiV1.BasicAuthRequest = reactive({ ...defaultAuthTokenRequest });

	function resetLogInBasic() {
		Object.assign(authTokenRequest, { ...defaultAuthTokenRequest });
		isInitLoginBasic.value = false;
	}

	function handleSuccess() {
		if (options?.onLogInSuccess !== undefined) {
			options.onLogInSuccess();
		}
	}

	async function logInBasic() {
		try {
			await authStore.getTokenBasic(authTokenRequest);
			handleSuccess();
		} catch (error) {
			handleError(error);
			resetLogInBasic();
			if (options?.onLogInError !== undefined) {
				options.onLogInError();
			}
		}
	}

	function initLogInBasic(loginDetails: Omit<ApiV1.BasicAuthRequest, 'remember_me'>) {
		Object.assign(authTokenRequest, { ...loginDetails });
		isInitLoginBasic.value = true;
	}

	async function logOut(options?: {
		redirect?: boolean;
		deleteServerToken?: boolean;
	}) {
		const { redirect, deleteServerToken } = {
			redirect: true,
			deleteServerToken: true,
			...options,
		};

		const impersonationToken = authStore.impersonationToken;
		if (impersonationToken) {
			await authStore.removeImpersonationToken(impersonationToken.uuid);
		}

		if (deleteServerToken) {
			try {
				await inflcr.logOut();
			} catch (error) {
				handleError(error);
			} finally {
				authStore.clearClientToken();
			}
		} else {
			authStore.clearClientToken();
		}

		if (redirect) await router.push({ name: 'LoginIndex' });

		if (pusher.connection.state === 'connected') {
			pusher.disconnect();
		}

		unsetUser();
		resetGainsight();
		resetCustomerIo();
		$ResetPinia().all?.();
	}

	onBeforeUnmount(() => resetLogInBasic());

	return {
		authTokenRequest,
		initLogInBasic,
		isInitLoginBasic,
		logInBasic,
		logOut,
		resetLogInBasic,
	};
}
